import type { AspectRatio } from './aspect-ratios.js';

export const PLATFORMS = [
  'FACEBOOK',
  'INSTAGRAM',
  'TIKTOK',
  'TELEGRAM',
  'X',
  'LINKEDIN',
  'YOUTUBE',
  'OTHER',
] as const;

export type Platform = (typeof PLATFORMS)[number];

/**
 * Tunable copy guidance, not claims about a platform's immutable limits.
 * Keep this in config so channel strategy can evolve without changing the
 * generator's data contract.
 */
export interface SocialCopyProfile {
  hashtagsMin: number;
  hashtagsMax: number;
  seoWeight: number;
  conversationalWeight: number;
  openingKeyword: boolean;
  descriptionDepth: 'short' | 'medium' | 'high';
  guidance: string;
}

export const SOCIAL_COPY_PROFILES: Record<Platform, SocialCopyProfile> = {
  FACEBOOK: { hashtagsMin: 2, hashtagsMax: 5, seoWeight: 0.65, conversationalWeight: 0.9, openingKeyword: false, descriptionDepth: 'medium', guidance: 'Use a standalone first-line hook, brief conversational paragraphs, and a relevant CTA. Invite discussion or sharing only when it fits.' },
  INSTAGRAM: { hashtagsMin: 3, hashtagsMax: 5, seoWeight: 0.85, conversationalWeight: 0.65, openingKeyword: true, descriptionDepth: 'short', guidance: 'Put the topic in the opening line, keep the caption scannable and visual-context-aware, and use a few precise niche/topic hashtags.' },
  TIKTOK: { hashtagsMin: 3, hashtagsMax: 5, seoWeight: 0.95, conversationalWeight: 0.55, openingKeyword: true, descriptionDepth: 'short', guidance: 'Write a natural search-style caption that answers or frames a real viewer query. Align spoken and on-screen wording with the topic. Do not rely on generic discovery tags.' },
  TELEGRAM: { hashtagsMin: 0, hashtagsMax: 2, seoWeight: 0.35, conversationalWeight: 0.75, openingKeyword: false, descriptionDepth: 'medium', guidance: 'Write a useful, direct channel message; hashtags are optional and sparse.' },
  X: { hashtagsMin: 0, hashtagsMax: 2, seoWeight: 0.5, conversationalWeight: 0.8, openingKeyword: false, descriptionDepth: 'short', guidance: 'Lead with the point, stay concise, and invite a focused response only when relevant.' },
  LINKEDIN: { hashtagsMin: 2, hashtagsMax: 4, seoWeight: 0.7, conversationalWeight: 0.65, openingKeyword: true, descriptionDepth: 'medium', guidance: 'Use a clear professional insight, natural topic terminology, and a useful takeaway.' },
  YOUTUBE: { hashtagsMin: 1, hashtagsMax: 5, seoWeight: 1, conversationalWeight: 0.35, openingKeyword: true, descriptionDepth: 'high', guidance: 'Make title and first description lines specific to the video and its viewer value. Tags are supporting variants, not the main SEO mechanism.' },
  OTHER: { hashtagsMin: 0, hashtagsMax: 5, seoWeight: 0.5, conversationalWeight: 0.5, openingKeyword: false, descriptionDepth: 'medium', guidance: 'Use natural, audience-appropriate copy specific to the destination.' },
};

export const PUBLISHER_TYPES = [
  'SOCIAMONIALS',
  'YOUTUBE',
  'DIRECT',
  'RSS',
  'CSV',
  'MCP',
  'TELEGRAM',
  'OTHER',
] as const;

export type PublisherType = (typeof PUBLISHER_TYPES)[number];

export type ContentKind = 'TEXT' | 'IMAGE' | 'VIDEO';

export interface PlatformMeta {
  readonly label: string;
  /** Default content kind generated for this platform. */
  readonly defaultKind: ContentKind;
  /** Default aspect ratio for image/video content. */
  readonly defaultAspectRatio: AspectRatio;
  /** Soft character limit for captions/posts. */
  readonly maxLength: number;
  /** Whether the platform uses hashtags by convention. */
  readonly hashtagFriendly: boolean;
  readonly color: string;
}

export const PLATFORM_META: Record<Platform, PlatformMeta> = {
  FACEBOOK: {
    label: 'Facebook',
    defaultKind: 'TEXT',
    defaultAspectRatio: '16:9',
    maxLength: 2200,
    hashtagFriendly: true,
    color: '#1877F2',
  },
  INSTAGRAM: {
    label: 'Instagram',
    defaultKind: 'IMAGE',
    defaultAspectRatio: '4:5',
    maxLength: 2200,
    hashtagFriendly: true,
    color: '#E1306C',
  },
  TIKTOK: {
    label: 'TikTok',
    defaultKind: 'VIDEO',
    defaultAspectRatio: '9:16',
    // TikTok captions hold 2200 characters. The old 150 was a leftover from
    // the short-caption era and rejected generated BloodCrimes copy (250) that
    // TikTok itself accepts, so the post never reached the publisher.
    maxLength: 2200,
    hashtagFriendly: true,
    color: '#010101',
  },
  TELEGRAM: {
    label: 'Telegram',
    defaultKind: 'TEXT',
    defaultAspectRatio: '16:9',
    maxLength: 1024,
    hashtagFriendly: false,
    color: '#229ED9',
  },
  X: {
    label: 'X',
    defaultKind: 'TEXT',
    defaultAspectRatio: '16:9',
    maxLength: 280,
    hashtagFriendly: true,
    color: '#111111',
  },
  LINKEDIN: {
    label: 'LinkedIn',
    defaultKind: 'TEXT',
    defaultAspectRatio: '1:1',
    maxLength: 3000,
    hashtagFriendly: true,
    color: '#0A66C2',
  },
  YOUTUBE: {
    label: 'YouTube',
    defaultKind: 'VIDEO',
    defaultAspectRatio: '16:9',
    maxLength: 5000,
    hashtagFriendly: true,
    color: '#FF0000',
  },
  OTHER: {
    label: 'Other',
    defaultKind: 'TEXT',
    defaultAspectRatio: '1:1',
    maxLength: 5000,
    hashtagFriendly: false,
    color: '#6B7280',
  },
};

export function isPlatform(value: string): value is Platform {
  return (PLATFORMS as readonly string[]).includes(value);
}
